import { Children, type ReactNode } from 'react'
import { deriveRunbookRefAlias } from '@/tiptap/tiptap-extensions/common/runbook-ref-mention/derive-alias'
import { cn } from '@/lib/utils'
import { chatStreamdownComponents } from '../../ChatLink'

/**
 * Canonical runbook ref grammar: `@<scope>` + one or more `.key` / `[index]`
 * segments. The leading `(?<!\w)` keeps it from matching inside an email/handle
 * (`a@params.com`). Mirrors the resolver grammar
 * (packages/types/src/runbook-refs.ts) and the tokenizer the runbook prompt
 * editor uses.
 */
const REF_TOKEN_RE =
  /(?<!\w)@(?:params|step|run|env|ces|event)(?:\.[A-Za-z0-9_-]+|\[\d+\])+/g

/**
 * Read-only counterpart to the editor's `runbookRefMention` node view: renders
 * `@scope.path` as a soft cobalt pill showing the friendly alias, with the
 * canonical ref on hover.
 */
export function RunbookRefPill({ refValue }: { refValue: string }) {
  return (
    <span
      title={refValue}
      className='mx-px inline-flex items-center rounded-sm bg-cobalt-tonal px-1.5 py-px align-baseline text-[12px] font-medium whitespace-nowrap text-cobalt-strong'
    >
      @{deriveRunbookRefAlias(refValue)}
    </span>
  )
}

/** Split one text run into plain text + ref pills. */
function tokenize(text: string, keyPrefix: string): ReactNode {
  REF_TOKEN_RE.lastIndex = 0
  if (!REF_TOKEN_RE.test(text)) return text
  REF_TOKEN_RE.lastIndex = 0
  const parts: ReactNode[] = []
  let cursor = 0
  let match: RegExpExecArray | null
  while ((match = REF_TOKEN_RE.exec(text)) !== null) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index))
    parts.push(
      <RunbookRefPill key={`${keyPrefix}-${match.index}`} refValue={match[0]} />,
    )
    cursor = match.index + match[0].length
  }
  if (cursor < text.length) parts.push(text.slice(cursor))
  return parts
}

/**
 * Only direct string children are tokenized — nested elements are left alone so
 * links and inline code keep their own rendering. The inline formatting
 * elements are overridden below, so their text is pill-ified by their own
 * renderer.
 */
function withPills(children: ReactNode): ReactNode {
  return Children.map(children, (child, i) =>
    typeof child === 'string' ? tokenize(child, String(i)) : child,
  )
}

/**
 * Streamdown components for markdown that carries runbook `@`-refs (the
 * procedure plan preview): `chatStreamdownComponents` plus ref tokens rendered
 * as pills. The `li` / `strong` overrides keep Streamdown's own default classes
 * (`py-1 [&>p]:inline`, and `strong` rendering as a semibold span) so only the
 * ref rendering changes.
 */
export const runbookRefStreamdownComponents = {
  ...chatStreamdownComponents,
  p: ({ node: _node, children, ...props }: any) => (
    <p {...props}>{withPills(children)}</p>
  ),
  li: ({ node: _node, children, className, ...props }: any) => (
    <li className={cn('py-1 [&>p]:inline', className)} {...props}>
      {withPills(children)}
    </li>
  ),
  strong: ({ node: _node, children, className, ...props }: any) => (
    <span className={cn('font-semibold', className)} {...props}>
      {withPills(children)}
    </span>
  ),
  em: ({ node: _node, children, ...props }: any) => (
    <em {...props}>{withPills(children)}</em>
  ),
}
